import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react';
import { cmsAPI } from '../utils/api';

const defaultTestimonials = [
  { id: 1, name: 'Priya Patel', role: 'Business Analyst', company: 'FinanceCorp', rating: 5, text: 'The classroom program at iStockly is outstanding.' },
  { id: 2, name: 'Amit Kumar', role: 'Investment Manager', company: 'WealthMax', rating: 5, text: 'iStockly stands out because the teaching is practical and clear.' },
  { id: 3, name: 'Sneha Gupta', role: 'Entrepreneur', company: 'StartupHub', rating: 5, text: 'The mentorship program connected me with experienced market professionals.' },
];

function avatarFor(name) {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=EAB308&color=000&size=80&bold=true`;
}

function adapt(item) {
  return {
    ...item,
    text: item.quote || item.text || '',
    image: item.avatar_url || item.image || avatarFor(item.name),
    isVideo: item.card_type === 'video' && Boolean(item.video_url),
    videoKind: item.video_source,
  };
}

function VideoFrame({ testimonial }) {
  if (!testimonial.isVideo) {
    return (
      <div className="flex aspect-video items-center justify-center rounded-xl border border-dashed border-zinc-700 bg-zinc-950">
        <Quote className="h-14 w-14 text-yellow-400/40" />
      </div>
    );
  }

  if (testimonial.videoKind === 'upload') {
    return (
      <video className="aspect-video w-full rounded-xl bg-black object-cover" controls preload="metadata" poster={testimonial.poster_url || undefined}>
        <source src={testimonial.video_url} />
      </video>
    );
  }

  return (
    <iframe
      className="aspect-video w-full rounded-xl bg-black"
      src={testimonial.video_url}
      title={`${testimonial.name} testimonial video`}
      loading="lazy"
      allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
      referrerPolicy="strict-origin-when-cross-origin"
      allowFullScreen
    />
  );
}

function extractItems(response) {
  const payload = response?.data?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

const Testimonials = ({ items }) => {
  const [backendItems, setBackendItems] = useState([]);
  const sourceItems = items?.length ? items : backendItems;
  const testimonials = useMemo(() => (sourceItems?.length ? sourceItems : defaultTestimonials).map(adapt), [sourceItems]);
  const [active, setActive] = useState(0);
  const current = testimonials[active] || testimonials[0];
  const go = (direction) => setActive((index) => (index + direction + testimonials.length) % testimonials.length);

  useEffect(() => {
    if (items?.length) return undefined;

    const controller = new AbortController();
    cmsAPI.testimonials({ per_page: 12 }, { signal: controller.signal })
      .then((response) => setBackendItems(extractItems(response)))
      .catch((error) => {
        if (error.code !== 'ERR_CANCELED') setBackendItems([]);
      });

    return () => controller.abort();
  }, [items]);

  useEffect(() => {
    setActive(0);
  }, [testimonials.length]);

  return (
    <section className="relative overflow-hidden bg-gray-900 py-20" aria-labelledby="testimonials-heading">
      <div className="absolute inset-0">
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.05, 0.1, 0.05] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute right-1/4 top-1/3 h-72 w-72 rounded-full bg-yellow-400 blur-3xl"
        />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12 text-center"
        >
          <h2 id="testimonials-heading" className="text-4xl font-bold text-white lg:text-6xl">
            <strong>What Our</strong> <strong className="text-yellow-400">Students</strong> <strong>Say</strong>
          </h2>
        </motion.div>

        <div className="mx-auto flex max-w-5xl items-center gap-3 sm:gap-5">
          <button
            type="button"
            onClick={() => go(-1)}
            className="z-10 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-yellow-400/40 bg-black/70 text-white shadow-lg transition hover:border-yellow-400 hover:bg-yellow-400 hover:text-black sm:h-14 sm:w-14"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>

          <motion.article
            key={current.id}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="min-w-0 flex-1 overflow-hidden rounded-2xl border border-yellow-400/20 bg-black/55 p-4 shadow-2xl shadow-black/30"
          >
            <VideoFrame testimonial={current} />
            <div className="grid gap-5 p-3 sm:grid-cols-[auto_1fr] sm:items-start">
              <img src={current.image} alt={current.name} className="h-16 w-16 rounded-full border-2 border-yellow-400/40 object-cover" />
              <div>
                <div className="mb-2 flex items-center gap-1">
                  {[...Array(current.rating || 5)].map((_, index) => <Star key={index} className="h-4 w-4 fill-current text-yellow-400" />)}
                </div>
                {current.text && <p className="text-base leading-relaxed text-gray-200">"{current.text}"</p>}
                <p className="mt-4 font-semibold text-white">{current.name}</p>
                <p className="text-sm text-gray-400">{[current.role, current.company].filter(Boolean).join(' - ')}</p>
              </div>
            </div>
          </motion.article>

          <button
            type="button"
            onClick={() => go(1)}
            className="z-10 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-yellow-400/40 bg-black/70 text-white shadow-lg transition hover:border-yellow-400 hover:bg-yellow-400 hover:text-black sm:h-14 sm:w-14"
            aria-label="Next testimonial"
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
